/**
 * Return a jagged array grouping items together based on a condition callback
 * @param array The array to be grouped
 * @param condition The condition callback that two items need to satisfy in order to be grouped together
 * @param exclude
 * Default 'false'. This flips the condition to be exclusive rather than inclusive
 * (ie. if two items meet the condition they'll never be grouped together)
 */
export function groupByCondition<T>(
    array: T[],
    condition: (item1: T, item2: T) => boolean,
    { exclude = false }: { exclude?: boolean } = {},
): T[][] {
    return array.reduce((groupedArray: T[][], item) => {
        // Check if the current item meets the condition with any groups
        const index = groupedArray.findIndex((group) => {
            const meetsCondition = group.some((groupItem) => condition(item, groupItem));
            return exclude ? !meetsCondition : meetsCondition;
        });

        if (index === -1) {
            // If the item doesn't meet the condition of any existing groups, create a new group
            return [...groupedArray, [item]];
        }

        // Otherwise, add it to the first group with which the item met the condition
        groupedArray[index] = [...groupedArray[index], item];
        return groupedArray;
    }, []);
}
